/**
 * Hook for managing the public interview chat experience.
 *
 * Handles:
 * - Token resolution on mount
 * - Starting / resuming the interview
 * - SSE streaming with suggested answer chips
 * - Review & verify step
 * - Interview completion and summary
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import publicInterviewService, {
  INTERVIEW_RESPONSE_MAX_CHARS,
  INTERVIEW_RESPONSE_TOO_LONG_MESSAGE,
} from '@/lib/api/publicInterview';
import type {
  PublicInterviewerInfo,
  PublicStreamEvent,
  TopicInfo,
  TopicReviewSummary,
  SidebarKnowledge,
  KnowledgeDelta,
} from '@/lib/api/publicInterview';

// ========================================
// TYPES
// ========================================

export type InterviewPhase =
  | 'loading'
  | 'email'
  | 'starting'
  | 'chatting'
  | 'generating_review'
  | 'reviewing'
  | 'confirming'
  | 'complete';

export interface ChatMessage {
  id: string;
  speaker: 'interviewer' | 'user';
  content: string;
  timestamp: Date;
  attachments?: File[];
  isStreaming?: boolean;
  suggestedAnswers?: string[];
  suggestionSelectionType?: 'single' | 'multi';
  knowledgeDelta?: KnowledgeDelta;
}

/**
 * Optional pre-bootstrapped session passed to `usePublicInterview` when a
 * caller (e.g. the gap-training modal) has already created the session
 * server-side and just wants the chat UI to render directly into 'chatting'
 * — skipping token-resolution → email-gate → startOrResume.
 *
 * The hook initializes its state from this seed exactly once per
 * `sessionId`, guarded by a ref so re-renders never re-seed and clobber
 * accumulated chat state.
 */
export interface SeededSession {
  sessionId: string;
  firstQuestion: string;
  suggestedAnswers: string[];
  suggestionSelectionType: 'single' | 'multi';
  totalTopics: number;
  currentTopicIndex: number;
  topics: TopicInfo[];
  topicConfidence: Record<string, number>;
  sidebarKnowledge: SidebarKnowledge | null;
  /** Optional interviewer info to display in the chat header without a
   * separate `resolveToken` round-trip. */
  interviewerName?: string | null;
  interviewerDescription?: string | null;
}

const createMessageId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

// ========================================
// HOOK
// ========================================

export function usePublicInterview(token: string, seed?: SeededSession) {
  // Phase-based state (replaces isLoading, isStarting, isComplete, isCompleting)
  const [phase, setPhase] = useState<InterviewPhase>('loading');

  // Core state
  const [interviewerInfo, setInterviewerInfo] = useState<PublicInterviewerInfo | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [totalTopics, setTotalTopics] = useState(0);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [topics, setTopics] = useState<TopicInfo[]>([]);
  const [topicConfidence, setTopicConfidence] = useState<Record<string, number>>({});

  // Orthogonal booleans (overlay on top of phase, not exclusive)
  const [isStreaming, setIsStreaming] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isUpdatingReview, setIsUpdatingReview] = useState(false);

  // Review state
  const [reviewSummaries, setReviewSummaries] = useState<TopicReviewSummary[]>([]);

  // Knowledge sidebar state
  const [sidebarKnowledge, setSidebarKnowledge] = useState<SidebarKnowledge | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const reviewTransitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // One-shot guard for the optional `seed` initialization. Stores the
  // session_id we already initialized for; subsequent renders with the
  // same session_id are no-ops, which prevents accidental re-seeding from
  // clobbering accumulated chat state when the parent re-renders.
  const seededSessionIdRef = useRef<string | null>(null);

  // ========================================
  // MESSAGE HELPERS
  // ========================================

  const addMessage = useCallback((msg: Omit<ChatMessage, 'id' | 'timestamp'>): string => {
    const id = createMessageId();
    setMessages((prev) => [...prev, { ...msg, id, timestamp: new Date() }]);
    return id;
  }, []);

  const updateMessage = useCallback((id: string, updates: Partial<ChatMessage>) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...updates } : m)));
  }, []);

  const clearSuggestedAnswers = useCallback(() => {
    setMessages((prev) =>
      prev.map((m) => (m.suggestedAnswers ? { ...m, suggestedAnswers: undefined } : m))
    );
  }, []);

  // ========================================
  // TOKEN RESOLUTION
  // ========================================

  useEffect(() => {
    if (!token) return;
    // When a `seed` is provided, the seed effect below handles all
    // initialization (interviewer info, session id, first message, phase).
    // Skipping `resolveToken` avoids a wasted round-trip and a transient
    // 'email' phase flicker before we override to 'chatting'.
    if (seed) return;

    let cancelled = false;

    (async () => {
      setPhase('loading');
      setError(null);
      try {
        const info = await publicInterviewService.resolveToken(token);
        if (!cancelled) {
          setInterviewerInfo(info);
          setTotalTopics(info.total_topics);
          if (info.topics?.length) setTopics(info.topics);
          setPhase('email');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Interview not found');
          setPhase('email');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // `seed` intentionally omitted: this effect must not re-fire if `seed`
    // is later set/cleared on a re-render. The seed effect below handles
    // the seed path; this effect owns only the unseeded token-resolution
    // path which is one-shot per token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ========================================
  // SEED INITIALIZATION (one-shot per session_id)
  // ========================================
  //
  // When a `seed` is provided (e.g. by the gap-training modal which
  // pre-creates the session via the authenticated start endpoint), bypass
  // the token-resolution → email-gate → startOrResume flow and initialize
  // chat state directly. Guarded by `seededSessionIdRef` so re-renders
  // never re-seed and clobber accumulated messages mid-conversation.

  useEffect(() => {
    if (!seed) return;
    if (seededSessionIdRef.current === seed.sessionId) return;
    seededSessionIdRef.current = seed.sessionId;

    setInterviewerInfo({
      name: seed.interviewerName ?? null,
      description: seed.interviewerDescription ?? null,
      logoUrl: null,
      total_topics: seed.totalTopics,
      topics: seed.topics,
    });
    setSessionId(seed.sessionId);
    setTotalTopics(seed.totalTopics);
    setCurrentTopicIndex(seed.currentTopicIndex);
    if (seed.topics?.length) setTopics(seed.topics);
    setTopicConfidence(seed.topicConfidence);
    setSidebarKnowledge(seed.sidebarKnowledge);
    setMessages([]);
    addMessage({
      speaker: 'interviewer',
      content: seed.firstQuestion,
      suggestedAnswers:
        seed.suggestedAnswers.length > 0 ? seed.suggestedAnswers : undefined,
      suggestionSelectionType: seed.suggestionSelectionType,
    });
    setError(null);
    setPhase('chatting');
  }, [seed, addMessage]);

  // Cleanup: cancel any pending review transition timeout on unmount
  useEffect(() => {
    return () => {
      if (reviewTransitionTimeoutRef.current) clearTimeout(reviewTransitionTimeoutRef.current);
    };
  }, []);

  // ========================================
  // ENTER REVIEW
  // ========================================

  const enterReview = useCallback(async () => {
    if (!token || !sessionId) return;
    if (phaseRef.current !== 'chatting') return; // guard: prevent double-trigger (use ref to avoid stale closure)

    setPhase('generating_review');
    setError(null);

    try {
      const result = await publicInterviewService.generateReview(token, sessionId);
      setReviewSummaries(result.topic_summaries);
      setTopicConfidence(result.topic_confidence);
      setPhase('reviewing');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate review');
      // Fall back to chatting if review generation fails
      setPhase('chatting');
    }
  }, [token, sessionId]);

  // ========================================
  // START OR RESUME INTERVIEW
  // ========================================

  const startOrResume = useCallback(
    async (email: string, name?: string, options?: { forceNew?: boolean }) => {
      if (!token) return;

      setPhase('starting');
      setError(null);
      setSummary(null);
      setReviewSummaries([]);
      setMessages([]);
      setTopicConfidence({});

      try {
        if (options?.forceNew) {
          const freshSession = await publicInterviewService.startInterview(
            token,
            name,
            email,
            'open_questions'
          );

          if (freshSession.no_open_questions || !freshSession.session_id || !freshSession.first_question) {
            if (freshSession.sidebar_knowledge) setSidebarKnowledge(freshSession.sidebar_knowledge);
            setSummary(freshSession.message || 'There are no open interview questions right now.');
            addMessage({
              speaker: 'interviewer',
              content: freshSession.message || 'There are no open interview questions right now.',
            });
            setPhase('complete');
            return;
          }

          setSessionId(freshSession.session_id);
          setTotalTopics(freshSession.total_topics);
          setCurrentTopicIndex(freshSession.current_topic_index);
          if (freshSession.topics?.length) setTopics(freshSession.topics);
          if (freshSession.topic_confidence) setTopicConfidence(freshSession.topic_confidence);
          if (freshSession.sidebar_knowledge) setSidebarKnowledge(freshSession.sidebar_knowledge);

          sessionStorage.setItem(`interview-session-${token}`, freshSession.session_id);
          sessionStorage.setItem(`interview-email-${token}`, email);

          addMessage({
            speaker: 'interviewer',
            content: freshSession.first_question,
            suggestedAnswers:
              freshSession.suggested_answers?.length > 0 ? freshSession.suggested_answers : undefined,
            suggestionSelectionType: freshSession.suggestion_selection_type || 'single',
          });
          setPhase('chatting');
          return;
        }

        const result = await publicInterviewService.startOrResume(token, email, name, 'default');

        if (result.no_open_questions || !result.session_id) {
          if (result.sidebar_knowledge) setSidebarKnowledge(result.sidebar_knowledge);
          setSummary(result.message || 'There are no open interview questions right now.');
          addMessage({
            speaker: 'interviewer',
            content: result.message || 'There are no open interview questions right now.',
          });
          setPhase('complete');
          return;
        }

        setSessionId(result.session_id);
        setTotalTopics(result.total_topics);
        setCurrentTopicIndex(result.current_topic_index);
        if (result.topics?.length) setTopics(result.topics);
        if (result.topic_confidence) setTopicConfidence(result.topic_confidence);
        if (result.sidebar_knowledge) setSidebarKnowledge(result.sidebar_knowledge);

        // Persist for page refresh resilience
        sessionStorage.setItem(`interview-session-${token}`, result.session_id);
        sessionStorage.setItem(`interview-email-${token}`, email);

        if (result.is_resumed && result.entries.length > 0) {
          // Resumed session: reconstruct message history with knowledge deltas
          const historicalMessages: ChatMessage[] = result.entries.map((entry) => ({
            id: entry.id,
            speaker: entry.speaker,
            content: entry.content,
            timestamp: new Date(entry.created_at),
            isStreaming: false,
          }));

          // Reconstruct knowledgeDelta from user entries' extracted_insights
          // and attach to the next interviewer message (matches live streaming behavior)
          for (let i = 0; i < result.entries.length; i++) {
            const entry = result.entries[i];
            if (
              entry.speaker !== 'user' ||
              !entry.extracted_insights ||
              !entry.topic_id ||
              Object.keys(entry.extracted_insights).length === 0
            ) continue;

            const insights = entry.extracted_insights;
            const newInsights = Object.entries(insights)
              .filter(([, v]) => v)
              .map(([field, value]) => ({ field, value, source_entry_id: entry.id }));
            if (newInsights.length === 0) continue;

            const delta: KnowledgeDelta = {
              topic_id: entry.topic_id,
              new_insights: newInsights,
              updated_fields: newInsights.map((i) => i.field),
            };

            // Find the next interviewer message to attach the delta to
            for (let j = i + 1; j < result.entries.length; j++) {
              if (result.entries[j].speaker === 'interviewer') {
                historicalMessages[j].knowledgeDelta = delta;
                break;
              }
            }
          }

          // Attach suggested answers to the last interviewer message
          if (result.suggested_answers?.length > 0) {
            const lastIdx = historicalMessages.reduce(
              (acc, msg, idx) => (msg.speaker === 'interviewer' ? idx : acc),
              -1
            );
            if (lastIdx >= 0) {
              historicalMessages[lastIdx].suggestedAnswers = result.suggested_answers;
              historicalMessages[lastIdx].suggestionSelectionType =
                result.suggestion_selection_type || 'single';
            }
          }

          setMessages(historicalMessages);

          // Check if resuming into review phase
          if (result.is_reviewing && result.review_summaries) {
            setReviewSummaries(result.review_summaries);
            setPhase('reviewing');
          } else {
            setPhase('chatting');
          }
        } else {
          // New session: show the first question, or finish gracefully if none remain.
          if (!result.first_question) {
            setSummary(result.message || 'There are no open interview questions right now.');
            addMessage({
              speaker: 'interviewer',
              content: result.message || 'There are no open interview questions right now.',
            });
            setPhase('complete');
            return;
          }

          addMessage({
            speaker: 'interviewer',
            content: result.first_question,
            suggestedAnswers:
              result.suggested_answers?.length > 0 ? result.suggested_answers : undefined,
            suggestionSelectionType: result.suggestion_selection_type || 'single',
          });
          setPhase('chatting');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start interview');
        setPhase('email');
      }
    },
    [token, addMessage]
  );

  // ========================================
  // SEND MESSAGE + STREAM RESPONSE
  // ========================================

  const sendMessage = useCallback(
    async (content: string, skipTopic = false, attachments: File[] = []): Promise<boolean> => {
      if (!token || !sessionId || isStreaming) return false;
      if (!skipTopic && content.length > INTERVIEW_RESPONSE_MAX_CHARS) {
        setError(INTERVIEW_RESPONSE_TOO_LONG_MESSAGE);
        return false;
      }

      // Cancel any existing stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // Clear suggested answers from all previous messages
      clearSuggestedAnswers();

      // Add user message
      addMessage({
        speaker: 'user',
        content: skipTopic
          ? "[Skipped topic]"
          : content || "Attached temporary context for this turn.",
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      // Create placeholder for the AI response
      const responseMsgId = addMessage({
        speaker: 'interviewer',
        content: '',
        isStreaming: true,
      });

      setIsStreaming(true);
      setError(null);

      let responseContent = '';

      try {
        const stream = publicInterviewService.streamResponse(
          token,
          sessionId,
          content,
          skipTopic,
          attachments
        );

        for await (const event of stream) {
          if (abortControllerRef.current?.signal.aborted) break;
          handleStreamEvent(event, responseMsgId, responseContent, (c) => {
            responseContent = c;
          });
        }

        // Mark message as not streaming
        updateMessage(responseMsgId, { isStreaming: false });
        return true;
      } catch (err) {
        updateMessage(responseMsgId, {
          content: 'Sorry, I encountered an error. Please try again.',
          isStreaming: false,
        });
        setError(err instanceof Error ? err.message : 'Failed to send message');
        return false;
      } finally {
        setIsStreaming(false);
      }
    },
    [token, sessionId, isStreaming, addMessage, updateMessage, clearSuggestedAnswers]
  );

  // ========================================
  // HANDLE STREAM EVENTS
  // ========================================

  const handleStreamEvent = useCallback(
    (
      event: PublicStreamEvent,
      responseMsgId: string,
      currentContent: string,
      setContent: (c: string) => void
    ) => {
      const { event_type, data } = event;

      switch (event_type) {
        case 'content': {
          const chunk = data.chunk || data.content;
          if (chunk) {
            const newContent = currentContent + chunk;
            setContent(newContent);
            updateMessage(responseMsgId, { content: newContent });
          }
          break;
        }

        case 'suggested_answers': {
          const suggestions = data.suggestions as string[] | undefined;
          if (suggestions?.length) {
            updateMessage(responseMsgId, {
              suggestedAnswers: suggestions,
              suggestionSelectionType: (data.selection_type as 'single' | 'multi') || 'single',
            });
          }
          break;
        }

        case 'topic_change': {
          if (data.is_complete) {
            // Instead of completing directly, enter the review step
            if (data.completion_message) {
              updateMessage(responseMsgId, {
                content: data.completion_message,
                isStreaming: false,
              });
            }
            // Trigger review after a short delay to show the completion message (deduped)
            if (reviewTransitionTimeoutRef.current) clearTimeout(reviewTransitionTimeoutRef.current);
            reviewTransitionTimeoutRef.current = setTimeout(() => {
              reviewTransitionTimeoutRef.current = null;
              enterReview();
            }, 1500);
          } else {
            if (data.current_topic_index !== undefined) {
              setCurrentTopicIndex(data.current_topic_index);
            }
            if (data.next_question) {
              updateMessage(responseMsgId, {
                content: data.next_question,
                isStreaming: false,
              });
            }
            // Suggested answers for the new topic's question
            const suggestions = data.suggested_answers as string[] | undefined;
            if (suggestions?.length) {
              updateMessage(responseMsgId, {
                suggestedAnswers: suggestions,
                suggestionSelectionType: (data.suggestion_selection_type as 'single' | 'multi') || 'single',
              });
            }
          }
          break;
        }

        case 'complete': {
          // Instead of completing directly, enter the review step
          if (data.completion_message) {
            updateMessage(responseMsgId, {
              content: data.completion_message,
              isStreaming: false,
            });
          }
          if (reviewTransitionTimeoutRef.current) clearTimeout(reviewTransitionTimeoutRef.current);
          reviewTransitionTimeoutRef.current = setTimeout(() => {
            reviewTransitionTimeoutRef.current = null;
            enterReview();
          }, 1500);
          break;
        }

        case 'error': {
          setError(data.message || 'Unknown error');
          updateMessage(responseMsgId, {
            content: data.message || 'An error occurred.',
            isStreaming: false,
          });
          break;
        }

        case 'progress':
          if (data.current_topic_index !== undefined) {
            setCurrentTopicIndex(data.current_topic_index);
          }
          break;

        case 'confidence_update':
          if (data.current_topic_index !== undefined) {
            setCurrentTopicIndex(data.current_topic_index);
          }
          if (data.topic_confidence) {
            setTopicConfidence((prev) => ({ ...prev, ...data.topic_confidence }));
          }
          // Apply knowledge_delta to sidebar and attach to message for banner
          if (data.knowledge_delta) {
            const delta = data.knowledge_delta as KnowledgeDelta;
            // Merge delta into current streaming message (accumulate across multiple events)
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== responseMsgId) return m;
                const existing = m.knowledgeDelta;
                if (!existing || existing.topic_id !== delta.topic_id) {
                  return { ...m, knowledgeDelta: delta };
                }
                const mergedInsights = [...existing.new_insights];
                const seenFields = new Set(mergedInsights.map((i) => i.field));
                for (const insight of delta.new_insights) {
                  if (!seenFields.has(insight.field)) {
                    mergedInsights.push(insight);
                    seenFields.add(insight.field);
                  }
                }
                return {
                  ...m,
                  knowledgeDelta: {
                    topic_id: delta.topic_id,
                    new_insights: mergedInsights,
                    updated_fields: [...new Set([...existing.updated_fields, ...delta.updated_fields])],
                  },
                };
              })
            );
            // Merge new insights into sidebarKnowledge
            setSidebarKnowledge((prev) => {
              if (!prev) return prev;
              const topicData = prev.topics[delta.topic_id];
              if (!topicData) return prev;
              const existingFields = new Set(topicData.live_insights.map((i) => i.field));
              const mergedInsights = [...topicData.live_insights];
              for (const insight of delta.new_insights) {
                if (!existingFields.has(insight.field)) {
                  mergedInsights.push(insight);
                  existingFields.add(insight.field);
                } else {
                  const idx = mergedInsights.findIndex((i) => i.field === insight.field);
                  if (idx >= 0) mergedInsights[idx] = insight;
                }
              }
              return {
                ...prev,
                topics: {
                  ...prev.topics,
                  [delta.topic_id]: { ...topicData, live_insights: mergedInsights },
                },
              };
            });
          }
          break;

        default:
          break;
      }
    },
    [updateMessage, enterReview]
  );

  // ========================================
  // SELECT SUGGESTED ANSWER
  // ========================================

  const selectSuggestedAnswer = useCallback(
    (answer: string) => {
      sendMessage(answer);
    },
    [sendMessage]
  );

  const submitMultiSelectAnswers = useCallback(
    (answers: string[]) => {
      if (answers.length === 0) return;
      const bulletList = answers.map((a) => `- ${a}`).join('\n');
      sendMessage(bulletList);
    },
    [sendMessage]
  );

  // ========================================
  // SKIP TOPIC
  // ========================================

  const skipTopic = useCallback(() => {
    sendMessage('[skip]', true);
  }, [sendMessage]);

  // ========================================
  // NAVIGATE TO TOPIC
  // ========================================

  const navigateToTopic = useCallback(
    async (topicIndex: number) => {
      if (!token || !sessionId || isStreaming || isNavigating) return;

      setIsNavigating(true);
      setError(null);
      clearSuggestedAnswers();

      try {
        const result = await publicInterviewService.navigateToTopic(token, sessionId, topicIndex);

        setCurrentTopicIndex(result.current_topic_index);

        // Add the new question as an interviewer message
        addMessage({
          speaker: 'interviewer',
          content: result.question,
          suggestedAnswers:
            result.suggested_answers?.length > 0 ? result.suggested_answers : undefined,
          suggestionSelectionType: result.suggestion_selection_type || 'single',
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to navigate to topic');
      } finally {
        setIsNavigating(false);
      }
    },
    [token, sessionId, isStreaming, isNavigating, addMessage, clearSuggestedAnswers]
  );

  // ========================================
  // REVIEW ACTIONS
  // ========================================

  const updateReviewTopic = useCallback(
    async (topicId: string, editedSummary: string) => {
      if (!token || !sessionId) return;

      // Capture previous value before optimistic update so rollback restores it, not null
      const previousEdit = reviewSummaries.find((s) => s.topic_id === topicId)?.edited_summary ?? null;

      // Optimistic update
      setReviewSummaries((prev) =>
        prev.map((s) =>
          s.topic_id === topicId ? { ...s, edited_summary: editedSummary } : s
        )
      );
      setIsUpdatingReview(true);

      try {
        const result = await publicInterviewService.updateReviewTopic(
          token, sessionId, topicId, editedSummary
        );
        setTopicConfidence(result.topic_confidence);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save edit');
        // Restore previous value (not null) so prior saved edits are preserved
        setReviewSummaries((prev) =>
          prev.map((s) =>
            s.topic_id === topicId ? { ...s, edited_summary: previousEdit } : s
          )
        );
      } finally {
        setIsUpdatingReview(false);
      }
    },
    [token, sessionId, reviewSummaries]
  );

  const confirmReview = useCallback(async () => {
    if (!token || !sessionId) return;

    setPhase('confirming');
    setError(null);

    try {
      const result = await publicInterviewService.confirmReview(token, sessionId);
      setSummary(result.summary);
      setPhase('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm review');
      setPhase('reviewing');
    }
  }, [token, sessionId]);

  const backToChat = useCallback(async () => {
    if (!token || !sessionId) return;

    try {
      await publicInterviewService.backToChat(token, sessionId);
      setPhase('chatting');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to return to chat');
    }
  }, [token, sessionId]);

  // ========================================
  // SAVE SIDEBAR CORRECTION
  // ========================================

  const saveCorrection = useCallback(
    async (topicId: string | null, fieldPath: string, correctedValue: any) => {
      if (!token || !sessionId) return;

      try {
        await publicInterviewService.saveCorrection(
          token, sessionId, fieldPath, correctedValue, topicId
        );
        // Update sidebarKnowledge locally with the correction
        setSidebarKnowledge((prev) => {
          if (!prev) return prev;
          const parts = fieldPath.split('.');
          if (parts.length !== 2) return prev;
          const [section, field] = parts;

          // Update company_summary if applicable
          if (section === 'company_profile' && field in prev.company_summary) {
            return {
              ...prev,
              company_summary: {
                ...prev.company_summary,
                [field as keyof typeof prev.company_summary]: correctedValue,
              },
            };
          }

          // Update only the specific topic's synthesis_sections
          if (topicId && prev.topics[topicId]) {
            const topicData = prev.topics[topicId];
            if (section in topicData.synthesis_sections) {
              return {
                ...prev,
                topics: {
                  ...prev.topics,
                  [topicId]: {
                    ...topicData,
                    synthesis_sections: {
                      ...topicData.synthesis_sections,
                      [section]: {
                        ...(typeof topicData.synthesis_sections[section] === 'object'
                          ? topicData.synthesis_sections[section]
                          : {}),
                        [field]: correctedValue,
                      },
                    },
                  },
                },
              };
            }
          }
          return prev;
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save correction');
      }
    },
    [token, sessionId]
  );

  // ========================================
  // COMPLETE INTERVIEW (legacy, kept for direct completion)
  // ========================================

  const completeInterview = useCallback(async () => {
    if (!token || !sessionId) return;

    setPhase('confirming');
    setError(null);

    try {
      const result = await publicInterviewService.completeInterview(token, sessionId);
      setSummary(result.summary);
      setPhase('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete interview');
      setPhase('chatting');
    }
  }, [token, sessionId]);

  return {
    // Phase
    phase,

    // Derived convenience booleans
    isLoading: phase === 'loading',
    isStarting: phase === 'starting',
    isComplete: phase === 'complete',
    isReviewing: phase === 'reviewing',
    isGeneratingReview: phase === 'generating_review',
    isConfirming: phase === 'confirming',

    // Core state
    interviewerInfo,
    messages,
    sessionId,
    summary,
    error,
    totalTopics,
    currentTopicIndex,
    topics,
    topicConfidence,

    // Orthogonal state
    isStreaming,
    isNavigating,
    isUpdatingReview,

    // Review state
    reviewSummaries,

    // Knowledge sidebar
    sidebarKnowledge,
    saveCorrection,

    // Actions
    startOrResume,
    sendMessage,
    selectSuggestedAnswer,
    submitMultiSelectAnswers,
    skipTopic,
    navigateToTopic,
    completeInterview,
    enterReview,
    updateReviewTopic,
    confirmReview,
    backToChat,
  };
}
